import { useState, useEffect, useCallback, useRef } from 'react'

interface VisualViewportState {
  height: number
  keyboardHeight: number
  isKeyboardOpen: boolean
}

/**
 * True on iOS running as an installed (standalone) PWA. The stale
 * post-keyboard-close viewport-height bug this hook works around is
 * specific to that context: in a regular Safari tab the URL bar absorbs
 * the offset and `visualViewport.height` recovers on its own. We compute
 * this once — it can't change within a session — and use it to gate the
 * height override so Android, desktop, and iOS-Safari-tab keep the plain
 * `visualViewport.height` behavior (no risk of masking a legitimate
 * viewport shrink from split-screen / Stage Manager / URL-bar show-hide).
 */
let _isIosStandaloneCache: boolean | null = null
function isIosStandalonePwa(): boolean {
  if (_isIosStandaloneCache !== null) return _isIosStandaloneCache
  try {
    const nav = window.navigator as Navigator & { standalone?: boolean }
    const ua = nav.userAgent || ''
    const isIos =
      /iP(hone|ad|od)/.test(ua) ||
      // iPadOS 13+ reports a desktop UA but is still iOS under the hood.
      (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)
    const standalone =
      (!!window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      nav.standalone === true
    _isIosStandaloneCache = isIos && standalone
  } catch {
    _isIosStandaloneCache = false
  }
  return _isIosStandaloneCache
}

/**
 * Hook to track the visual viewport height and detect mobile keyboard presence.
 * Uses the visualViewport API to detect when the keyboard opens/closes.
 * Includes iOS Safari specific handling.
 *
 * `enabled` (default true) gates all event-listener wiring. Pass the page's
 * `isMobile` so desktop attaches nothing — desktop never consumes this height
 * (the workspace page uses h-dvh there), and this keeps the hook inert off
 * mobile. It re-attaches automatically if the breakpoint flips, since the
 * flag is in the effect's dependency list.
 */
export function useVisualViewport(enabled: boolean = true): VisualViewportState {
  // Tallest viewport height seen with the keyboard closed = the true full
  // height for the current orientation. Used to override iOS's stale
  // post-keyboard-close height (see updateViewport). Re-baselined on
  // orientation change via the window 'resize' handler.
  const fullHeightRef = useRef(window.visualViewport?.height ?? window.innerHeight)
  // Pending focusout timer, tracked so we can clear it on unmount.
  const focusOutTimerRef = useRef<number | null>(null)

  const [state, setState] = useState<VisualViewportState>(() => {
    const height = window.visualViewport?.height ?? window.innerHeight
    return {
      height,
      keyboardHeight: 0,
      isKeyboardOpen: false,
    }
  })

  const updateViewport = useCallback(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const fullHeight = window.innerHeight
    const visibleHeight = viewport.height
    const heightDiff = fullHeight - visibleHeight
    // Detect the keyboard by visualViewport.height shrinking, NOT by
    // visualViewport.offsetTop. The offsetTop check used to be an OR-clause
    // to catch iOS auto-scrolling a low-positioned focused input into view.
    // But on iOS PWA, offsetTop is unreliable on the way out: after the
    // keyboard closes via swipe-down / Done / tap-outside, iOS sometimes
    // leaves offsetTop non-zero, pinning isKeyboardOpen=true and clamping
    // the page height until a manual scroll fires visualViewport.scroll.
    // That was the visible "input stuck slightly higher" symptom. heightDiff
    // alone reliably covers genuine keyboards (always > 100 px) and resets
    // to 0 on close via the resize event.
    const isKeyboardOpen = heightDiff > 100
    const keyboardHeight = isKeyboardOpen ? Math.max(heightDiff, 0) : 0

    // Track the tallest viewport height seen with the keyboard closed —
    // that's the true full height for the current orientation. Re-baselined
    // on orientation change by the window 'resize' handler below.
    if (!isKeyboardOpen && visibleHeight > fullHeightRef.current) {
      fullHeightRef.current = visibleHeight
    }

    // iOS PWA bug: after a swipe-down keyboard dismiss, both
    // window.innerHeight and visualViewport.height stay pinned ~60 px below
    // the true full height (e.g. 812 instead of 874) until a real
    // touch-scroll forces iOS to recompute. A programmatic scroll can't
    // trigger that recompute because the workspace layout is exactly
    // viewport-height (nothing to scroll). So instead of coaxing iOS, we
    // ignore its stale post-close value and use the cached true full height.
    //
    // Gated to iOS standalone PWA: elsewhere a sub-full closed-keyboard
    // height is legitimate (URL-bar show/hide in a Safari tab, iPad
    // split-screen / Stage Manager), and overriding it would clip content.
    // The `- 8` tolerance avoids overriding for sub-pixel rounding.
    const effectiveHeight =
      isIosStandalonePwa() && !isKeyboardOpen && visibleHeight < fullHeightRef.current - 8
        ? fullHeightRef.current
        : visibleHeight

    // iOS fires resize/scroll on every keyboard-animation frame. Bail out
    // when nothing changed so consumers (Workspace.tsx clamps page height
    // off this) don't re-render on every frame.
    setState((prev) =>
      prev.height === effectiveHeight &&
      prev.keyboardHeight === keyboardHeight &&
      prev.isKeyboardOpen === isKeyboardOpen
        ? prev
        : { height: effectiveHeight, keyboardHeight, isKeyboardOpen },
    )
  }, [])

  useEffect(() => {
    // Disabled (e.g. desktop): attach no listeners. The initial state stands
    // and is never consumed off mobile.
    if (!enabled) return

    const viewport = window.visualViewport

    if (!viewport) {
      // Fallback for browsers without visualViewport API
      // Use focus/blur events as a heuristic
      const handleFocus = (e: FocusEvent) => {
        const target = e.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          // Assume keyboard is open when input is focused
          setTimeout(() => {
            setState(prev => ({
              ...prev,
              isKeyboardOpen: true,
              keyboardHeight: 300, // Estimate
              height: window.innerHeight - 300,
            }))
          }, 300)
        }
      }

      const handleBlur = () => {
        setState({
          height: window.innerHeight,
          keyboardHeight: 0,
          isKeyboardOpen: false,
        })
      }

      document.addEventListener('focusin', handleFocus)
      document.addEventListener('focusout', handleBlur)

      return () => {
        document.removeEventListener('focusin', handleFocus)
        document.removeEventListener('focusout', handleBlur)
      }
    }

    // Use visualViewport API
    viewport.addEventListener('resize', updateViewport)
    viewport.addEventListener('scroll', updateViewport)

    // Window resize = orientation / window-size change (NOT keyboard — iOS
    // fires only visualViewport resize for the keyboard). If no input is
    // focused the keyboard can't be open, so the current height is the true
    // full height: re-baseline the cache. Without this, rotating to a
    // shorter viewport would be wrongly overridden by the taller previous
    // orientation's cached value.
    const onWindowResize = () => {
      const active = document.activeElement as HTMLElement | null
      const editing =
        !!active &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable)
      if (!editing) {
        fullHeightRef.current = window.visualViewport?.height ?? window.innerHeight
      }
      updateViewport()
    }
    window.addEventListener('resize', onWindowResize)

    // Safety net for the iOS PWA close path: when focus leaves the page
    // entirely (or moves to a non-editable element) the keyboard can't be
    // open regardless of what visualViewport currently reports. Defer
    // ~300 ms so iOS finishes the close animation, then re-run updateViewport
    // so the (iOS-gated) full-height override applies — instead of writing a
    // raw, possibly-still-stale window.innerHeight.
    const onFocusOut = () => {
      if (focusOutTimerRef.current !== null) {
        window.clearTimeout(focusOutTimerRef.current)
      }
      focusOutTimerRef.current = window.setTimeout(() => {
        focusOutTimerRef.current = null
        const active = document.activeElement as HTMLElement | null
        const stillEditing =
          !!active &&
          (active.tagName === 'INPUT' ||
            active.tagName === 'TEXTAREA' ||
            active.isContentEditable)
        if (stillEditing) return
        updateViewport()
      }, 300)
    }
    document.addEventListener('focusout', onFocusOut)

    // Initial check
    updateViewport()

    return () => {
      viewport.removeEventListener('resize', updateViewport)
      viewport.removeEventListener('scroll', updateViewport)
      window.removeEventListener('resize', onWindowResize)
      document.removeEventListener('focusout', onFocusOut)
      if (focusOutTimerRef.current !== null) {
        window.clearTimeout(focusOutTimerRef.current)
        focusOutTimerRef.current = null
      }
    }
  }, [updateViewport, enabled])

  return state
}
